import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Drawer, List, ListItem, ListItemIcon, ListItemText,
  ListItemButton, Collapse, Box, Typography, Divider, Badge, Avatar, Tooltip, Chip
} from '@mui/material';
import { getCachedMyGymPermissions } from '../utils/cachedData';
import { useBadges } from '../utils/badgeStore';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  CardMembership as CardMembershipIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  FitnessCenter as FitnessCenterIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  AutoAwesome as AIIcon,
  Inventory as InventoryIcon,
  ShoppingCart as POSIcon,
  SupervisorAccount as TrainerManagementIcon,
  BookOnline as BookingsIcon,
  Event as EventIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday,
  Chat as ChatIcon,
  Campaign as CampaignIcon,
  CardGiftcard as ReferralIcon,
  Person as PersonIcon,
  Circle as DotIcon,
  MoneyOff as ExpenseIcon,
  Announcement as AnnouncementIcon,
  EmojiEvents as RewardsIcon,
  ContactMail as ContactMailIcon,
  Fingerprint as FingerprintIcon,
  QueryStats as QueryStatsIcon,
  PhoneInTalk as WorklistIcon,
  Sensors as LiveBoardIcon,
  SmartToy as AiAssistantIcon,
  Autorenew as RenewalJourneyIcon,
  MonitorWeight as ProgressIcon,
  QrCode2 as MembershipCardIcon,
  EventAvailable as ClassBookingIcon,
  Leaderboard as LeaderboardIcon,
  RateReview as FeedbackIcon,
  FilterAlt as LeadFunnelIcon
} from '@mui/icons-material';

// ── Design Tokens ────────────────────────────────────────────────────────────
const SIDEBAR_BG     = '#0f172a';   // deep navy
const SIDEBAR_BG2    = '#1e293b';   // slightly lighter for hover
const ACTIVE_BG      = 'rgba(99,102,241,0.18)';
const ACTIVE_COLOR   = '#818cf8';   // indigo-400
const ACTIVE_BORDER  = '#6366f1';   // indigo-500
const TEXT_PRIMARY   = '#e2e8f0';
const TEXT_MUTED     = '#64748b';
const SECTION_COLOR  = '#475569';
const SUBITEM_BG     = '#0c1322';
const SUBITEM_ACTIVE = 'rgba(99,102,241,0.12)';

// ── Menu config ───────────────────────────────────────────────────────────────
const getMenuItems = (t) => ({
  dashboard: {
    id: 'dashboard', label: t('common.dashboard'),
    icon: <DashboardIcon fontSize="small" />, path: '/', subItems: [],
    section: 'main'
  },
  members: {
    id: 'members', label: t('admin.members'),
    icon: <PeopleIcon fontSize="small" />, path: '/members', subItems: [],
    section: 'management'
  },
  plans: {
    id: 'plans', label: t('admin.plans'),
    icon: <CardMembershipIcon fontSize="small" />, path: '/plans', subItems: [],
    section: 'management'
  },
  payments: {
    id: 'payments', label: t('admin.payments'),
    icon: <PaymentIcon fontSize="small" />, path: '/payments',
    subItems: [
      { label: 'All Payments', path: '/payments' },
      { label: 'Add Payment', path: '/payments/add' }
    ],
    section: 'management'
  },
  renewalmanagement: {
    id: 'renewalmanagement', label: 'Renewal Management',
    icon: <CalendarToday fontSize="small" />, path: '/renewal-management', subItems: [],
    section: 'management'
  },
  attendance: {
    id: 'attendance', label: t('admin.attendance'),
    icon: <CheckCircleIcon fontSize="small" />, path: '/attendance',
    subItems: [
      { label: 'Attendance Tracking', path: '/attendance' },
      { label: 'Attendance Reports', path: '/attendance/reports' }
    ],
    section: 'management'
  },
  trainers: {
    id: 'trainers', label: 'Staff & Trainers',
    icon: <FitnessCenterIcon fontSize="small" />, path: '/staff-trainers',
    subItems: [
      { label: 'All Users', path: '/users' },
      { label: 'Add User', path: '/users/add' },
      { label: 'User Login Management', path: '/users/login-management' }
    ],
    section: 'management'
  },
  trainermanagement: {
    id: 'trainermanagement', label: 'Trainer Management',
    icon: <TrainerManagementIcon fontSize="small" />, path: '/trainer-management', subItems: [],
    section: 'trainers'
  },
  trainerprofiles: {
    id: 'trainerprofiles', label: 'Trainer Profiles',
    icon: <PeopleIcon fontSize="small" />, path: '/trainer-profiles', subItems: [],
    section: 'trainers'
  },
  trainerbookings: {
    id: 'trainerbookings', label: 'Trainer Bookings',
    icon: <EventIcon fontSize="small" />, path: '/trainer-bookings', subItems: [],
    section: 'trainers'
  },
  trainerrevenue: {
    id: 'trainerrevenue', label: 'Trainer Revenue',
    icon: <AttachMoneyIcon fontSize="small" />, path: '/trainer-revenue', subItems: [],
    section: 'trainers'
  },
  reports: {
    id: 'reports', label: t('admin.reports'),
    icon: <AssessmentIcon fontSize="small" />, path: '/reports',
    subItems: [
      { label: 'Attendance Reports', path: '/reports' },
      { label: 'Payment Reports', path: '/reports/payments' }
    ],
    section: 'analytics'
  },
  inventory: {
    id: 'inventory', label: 'Inventory',
    icon: <InventoryIcon fontSize="small" />, path: '/inventory',
    subItems: [
      { label: 'Products', path: '/inventory' },
      { label: 'Bookings', path: '/inventory/bookings' },
      { label: 'Reports', path: '/inventory/reports' }
    ],
    section: 'analytics'
  },
  pos: {
    id: 'pos', label: 'POS System',
    icon: <POSIcon fontSize="small" />, path: '/pos', subItems: [],
    section: 'analytics'
  },
  analytics: {
    id: 'analytics', label: 'Analytics',
    icon: <QueryStatsIcon fontSize="small" />, path: '/analytics', subItems: [],
    section: 'analytics', adminOnly: true
  },
  worklist: {
    id: 'worklist', label: 'At-Risk Worklist',
    icon: <WorklistIcon fontSize="small" />, path: '/worklist', subItems: [],
    section: 'analytics', adminOnly: true
  },
  liveboard: {
    id: 'liveboard', label: 'Live Board',
    icon: <LiveBoardIcon fontSize="small" />, path: '/live-board', subItems: [],
    section: 'analytics', adminOnly: true
  },
  leadfunnel: {
    id: 'leadfunnel', label: 'Lead Funnel',
    icon: <LeadFunnelIcon fontSize="small" />, path: '/lead-funnel', subItems: [],
    section: 'analytics', adminOnly: true
  },
  aicoach: {
    id: 'aicoach', label: 'AI Fitness Coach',
    icon: <AIIcon fontSize="small" />, path: '/ai-coach', subItems: [],
    section: 'tools'
  },
  chat: {
    id: 'chat', label: t('chat.messages'),
    icon: <ChatIcon fontSize="small" />, path: '/chat', subItems: [],
    section: 'tools'
  },
  announcements: {
    id: 'announcements', label: 'Announcements',
    icon: <AnnouncementIcon fontSize="small" />, path: '/announcements', subItems: [],
    section: 'tools', adminOnly: true
  },
  campaigns: {
    id: 'campaigns', label: t('campaigns.campaigns'),
    icon: <CampaignIcon fontSize="small" />, path: '/campaigns', subItems: [],
    section: 'tools'
  },
  renewaljourney: {
    id: 'renewaljourney', label: 'Renewal Journey',
    icon: <RenewalJourneyIcon fontSize="small" />, path: '/renewal-journey', subItems: [],
    section: 'tools', adminOnly: true
  },
  classscheduling: {
    id: 'classscheduling', label: 'Class Scheduling',
    icon: <EventIcon fontSize="small" />, path: '/class-scheduling', subItems: [],
    section: 'tools', adminOnly: true
  },
  referrals: {
    id: 'referrals', label: t('referrals.referrals'),
    icon: <ReferralIcon fontSize="small" />, path: '/referrals', subItems: [],
    section: 'tools'
  },
  // Member-specific
  // Assigned per gym by superadmin (Permission Management → Member).
  // memberOnly: staff have no attendance of their own, so never show it to them.
  fingerprint: {
    id: 'fingerprint', label: 'Fingerprint Attendance',
    icon: <FingerprintIcon fontSize="small" />, path: '/member/fingerprint-attendance', subItems: [],
    section: 'member', memberOnly: true
  },
  aiassistant: {
    id: 'aiassistant', label: 'AI Assistant',
    icon: <AiAssistantIcon fontSize="small" />, path: '/member/assistant', subItems: [],
    section: 'member', memberOnly: true
  },
  products: {
    id: 'products', label: 'Product Catalog',
    icon: <POSIcon fontSize="small" />, path: '/member/products', subItems: [],
    section: 'member'
  },
  personaltrainer: {
    id: 'personaltrainer', label: 'Personal Trainer',
    icon: <FitnessCenterIcon fontSize="small" />, path: '/member/personal-trainer', subItems: [],
    section: 'member'
  },
  bookings: {
    id: 'bookings', label: 'My Bookings',
    icon: <BookingsIcon fontSize="small" />, path: '/member/bookings', subItems: [],
    section: 'member'
  },
  renewal: {
    id: 'renewal', label: 'Membership Renewal',
    icon: <CalendarToday fontSize="small" />, path: '/member/renewal', subItems: [],
    section: 'member'
  },
  membershipcard: {
    id: 'membershipcard', label: 'Membership Card',
    icon: <MembershipCardIcon fontSize="small" />, path: '/member/card', subItems: [],
    section: 'member', memberOnly: true
  },
  classbooking: {
    id: 'classbooking', label: 'Class Schedule',
    icon: <ClassBookingIcon fontSize="small" />, path: '/member/classes', subItems: [],
    section: 'member', memberOnly: true
  },
  progress: {
    id: 'progress', label: 'My Progress',
    icon: <ProgressIcon fontSize="small" />, path: '/member/progress', subItems: [],
    section: 'member', memberOnly: true
  },
  leaderboard: {
    id: 'leaderboard', label: 'Leaderboard',
    icon: <LeaderboardIcon fontSize="small" />, path: '/member/leaderboard', subItems: [],
    section: 'member', memberOnly: true
  },
  feedback: {
    id: 'feedback', label: 'Feedback',
    icon: <FeedbackIcon fontSize="small" />, path: '/member/feedback', subItems: [],
    section: 'member', memberOnly: true
  },
  rewards: {
    id: 'rewards', label: 'Rewards & Badges',
    icon: <RewardsIcon fontSize="small" />, path: '/member/rewards', subItems: [
      { label: 'My Rewards', path: '/member/rewards' },
      { label: 'Rewards Catalog', path: '/member/rewards-catalog' }
    ],
    section: 'member'
  },
  gamification: {
    id: 'gamification', label: 'Rewards & Badges',
    icon: <RewardsIcon fontSize="small" />, path: '/admin/gamification', subItems: [
      { label: 'Gamification', path: '/admin/gamification' },
      { label: 'Rewards Catalog', path: '/admin/rewards' }
    ],
    section: 'tools'
  },
  expenses: {
    id: 'expenses', label: 'Expenses',
    icon: <ExpenseIcon fontSize="small" />, path: '/expenses', subItems: [],
    section: 'analytics'
  },
  enquiries: {
    id: 'enquiries', label: 'Enquiries',
    icon: <ContactMailIcon fontSize="small" />, path: '/enquiries', subItems: [],
    section: 'members'
  },
  settings: {
    id: 'settings', label: t('common.settings'),
    icon: <SettingsIcon fontSize="small" />, path: '/settings', subItems: [],
    section: 'system'
  },
  emailSettings: {
    id: 'emailSettings', label: 'Email Settings',
    icon: <SettingsIcon fontSize="small" />, path: '/admin/email-settings', subItems: [],
    section: 'system'
  }
});

const SECTIONS = {
  main:       { label: null },           // no header for main
  management: { label: 'MANAGEMENT' },
  trainers:   { label: 'TRAINERS' },
  analytics:  { label: 'ANALYTICS' },
  tools:      { label: 'TOOLS' },
  member:     { label: 'MEMBER ZONE' },
  system:     { label: 'SYSTEM' }
};

// ── Component ─────────────────────────────────────────────────────────────────
const DynamicSidebar = ({ open, onClose, drawerWidth = 260, isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const menuItems = getMenuItems(t);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [allowedMenus, setAllowedMenus] = useState(null);

  // Chat unread badge comes from the shared poller (badgeStore) — no separate poll.
  const { unreadChatCount: unreadChat } = useBadges();

  useEffect(() => {
    let cancelled = false;
    const fetchPermissions = async () => {
      if (user?.gymId) {
        try {
          const data = await getCachedMyGymPermissions();
          if (cancelled) return;
          if (data.success && data.allowedMenus && Array.isArray(data.allowedMenus) && data.allowedMenus.length > 0) {
            setAllowedMenus(data.allowedMenus);
          } else {
            setAllowedMenus(null);
          }
        } catch {
          if (!cancelled) setAllowedMenus(null);
        }
      }
    };
    fetchPermissions();
    return () => { cancelled = true; };
  }, [user?.gymId]);

  const isMember = user?.role === 'member';

  const visibleMenuItems = (allowedMenus === null
    ? Object.values(menuItems)
    : allowedMenus.map(id => menuItems[id]).filter(Boolean)
  ).filter(item => isMember ? !item.adminOnly : !item.memberOnly);

  const handleMenuClick = (path) => navigate(path);
  const handleExpandClick = (menuId) => setExpandedMenus(prev => ({ ...prev, [menuId]: !prev[menuId] }));

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname === path || location.pathname.startsWith(path + '/');

  const isParentActive = (item) =>
    isActive(item.path) || item.subItems.some(s => isActive(s.path));

  // Group items by section preserving order
  const sections = [];
  const seen = new Set();
  visibleMenuItems.forEach(item => {
    const sec = item.section || 'main';
    if (!seen.has(sec)) {
      seen.add(sec);
      sections.push({ key: sec, label: SECTIONS[sec]?.label || null, items: [] });
    }
    sections.find(s => s.key === sec).items.push(item);
  });

  // User avatar initials
  const initials = user?.name ? user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : 'U';
  const roleLabel = user?.role === 'superadmin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : user?.role === 'gym_admin' ? 'Gym Admin' : 'Member';
  const roleColor = user?.role === 'superadmin' ? '#f59e0b' : user?.role?.includes('admin') ? '#818cf8' : '#34d399';

  const drawerContent = (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%',
      bgcolor: SIDEBAR_BG, color: TEXT_PRIMARY, overflow: 'hidden'
    }}>
      {/* Logo area (mobile only — desktop has top Navbar) */}
      {isMobile && (
        <Box sx={{
          px: 2.5, py: 2.5,
          background: 'linear-gradient(135deg, #312e81 0%, #1e1b4b 100%)',
          display: 'flex', alignItems: 'center', gap: 1.5
        }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #818cf8, #6366f1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 900, color: '#fff'
          }}>G</Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#fff', letterSpacing: 0.5 }}>
            Gym Manager
          </Typography>
        </Box>
      )}

      {/* User card */}
      <Box sx={{
        mx: 1.5, mt: isMobile ? 1.5 : 1.5, mb: 1,
        p: 1.5, borderRadius: 2,
        background: 'linear-gradient(135deg, #1e293b 0%, #1a2235 100%)',
        border: '1px solid rgba(99,102,241,0.2)',
        display: 'flex', alignItems: 'center', gap: 1.5
      }}>
        <Avatar sx={{
          width: 38, height: 38, fontSize: 14, fontWeight: 800,
          background: `linear-gradient(135deg, ${roleColor}88, ${roleColor})`,
          border: `2px solid ${roleColor}44`
        }}>
          {initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap sx={{ color: TEXT_PRIMARY, fontSize: 13 }}>
            {user?.name || 'User'}
          </Typography>
          <Chip
            label={roleLabel}
            size="small"
            sx={{
              height: 18, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
              bgcolor: `${roleColor}22`, color: roleColor, border: `1px solid ${roleColor}44`,
              '& .MuiChip-label': { px: 0.75 }
            }}
          />
        </Box>
        <Box sx={{
          width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e',
          boxShadow: '0 0 6px #22c55e88'
        }} />
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 1.5, mb: 0.5 }} />

      {/* Menu */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 0.5,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 4 }
      }}>
        {sections.map(({ key, label, items }) => (
          <Box key={key}>
            {label && (
              <Typography variant="caption" sx={{
                display: 'block', px: 2.5, pt: 2, pb: 0.5,
                color: SECTION_COLOR, fontWeight: 700, fontSize: 10, letterSpacing: 1.5
              }}>
                {label}
              </Typography>
            )}
            <List disablePadding>
              {items.map((menuItem) => {
                const active = isParentActive(menuItem);
                const expanded = expandedMenus[menuItem.id];
                return (
                  <React.Fragment key={menuItem.id}>
                    <ListItem disablePadding sx={{ px: 1, mb: 0.25 }}>
                      <ListItemButton
                        onClick={() => {
                          if (menuItem.subItems.length > 0) handleExpandClick(menuItem.id);
                          else handleMenuClick(menuItem.path);
                        }}
                        sx={{
                          borderRadius: 1.5, py: 0.85, px: 1.5, minHeight: 40,
                          bgcolor: active ? ACTIVE_BG : 'transparent',
                          borderLeft: active ? `3px solid ${ACTIVE_BORDER}` : '3px solid transparent',
                          transition: 'all 0.18s ease',
                          '&:hover': {
                            bgcolor: active ? ACTIVE_BG : 'rgba(255,255,255,0.05)',
                            pl: '14px'
                          }
                        }}
                      >
                        <ListItemIcon sx={{
                          minWidth: 34, color: active ? ACTIVE_COLOR : TEXT_MUTED,
                          transition: 'color 0.18s'
                        }}>
                          {menuItem.id === 'chat' && unreadChat > 0
                            ? <Badge badgeContent={unreadChat} color="error" max={99} sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 16, height: 16 } }}>
                                {menuItem.icon}
                              </Badge>
                            : menuItem.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={menuItem.label}
                          primaryTypographyProps={{
                            fontSize: 13, fontWeight: active ? 700 : 500,
                            color: active ? TEXT_PRIMARY : '#94a3b8',
                            noWrap: true
                          }}
                        />
                        {menuItem.subItems.length > 0 && (
                          expanded
                            ? <ExpandLess sx={{ fontSize: 16, color: TEXT_MUTED }} />
                            : <ExpandMore sx={{ fontSize: 16, color: TEXT_MUTED }} />
                        )}
                      </ListItemButton>
                    </ListItem>

                    {/* Sub-items */}
                    {menuItem.subItems.length > 0 && (
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 1, pr: 1, mb: 0.5 }}>
                          {menuItem.subItems.map((sub) => {
                            const subActive = isActive(sub.path);
                            return (
                              <ListItemButton
                                key={sub.path}
                                onClick={() => handleMenuClick(sub.path)}
                                sx={{
                                  borderRadius: 1.5, py: 0.6, pl: 4, pr: 1.5, mb: 0.25,
                                  bgcolor: subActive ? SUBITEM_ACTIVE : 'transparent',
                                  transition: 'all 0.15s',
                                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' }
                                }}
                              >
                                <Box sx={{
                                  width: 6, height: 6, borderRadius: '50%', mr: 1.5,
                                  bgcolor: subActive ? ACTIVE_COLOR : TEXT_MUTED,
                                  flexShrink: 0
                                }} />
                                <ListItemText
                                  primary={sub.label}
                                  primaryTypographyProps={{
                                    fontSize: 12, fontWeight: subActive ? 600 : 400,
                                    color: subActive ? ACTIVE_COLOR : '#64748b',
                                    noWrap: true
                                  }}
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
        ))}

        {visibleMenuItems.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: TEXT_MUTED, fontSize: 12 }}>
              No menus available.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{
        px: 2, py: 1.5,
        borderTop: '1px solid rgba(255,255,255,0.06)',
        bgcolor: '#0c1322'
      }}>
        <Typography variant="caption" sx={{ color: TEXT_MUTED, fontSize: 10, display: 'block', textAlign: 'center', lineHeight: 1.8 }}>
          © {new Date().getFullYear()} Fintech. All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onClose}
      anchor="left"
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? 0 : (open ? drawerWidth : 0),
        flexShrink: 0,
        whiteSpace: 'nowrap',
        transition: theme => theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: isMobile ? 0 : '64px',
          height: isMobile ? '100%' : 'calc(100% - 64px)',
          border: 'none',
          bgcolor: SIDEBAR_BG,
          overflow: 'hidden',
          transition: theme => theme.transitions.create('transform', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          transform: (!isMobile && !open) ? `translateX(-${drawerWidth}px)` : 'none',
          boxShadow: '4px 0 24px rgba(0,0,0,0.3)'
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default DynamicSidebar;
